import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useGetGroupByPart, useGetGroupDeviceList, useGetPartsAll, useGetRegisterRoles, useGetSearchRoles } from '../api/main'
import { theme } from '../styles/theme'
import LoadingDots from './Loading'

const DropdownContainer = styled.div<{ $isLoading?: boolean}>`
  position: relative;
  display: inline-block;
  flex:1;
  min-width: 150px;
  background: ${theme.colors.white};

  /* 로딩 중일 때 모자이크 효과 및 포인트 이벤트 금지 */
  /* filter: ${({ $isLoading }) => ($isLoading ? 'blur(10px)' : 'none')}; */
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};
`

const DropdownButton = styled.button`
  color: ${theme.colors.black};
  padding: 10px 20px;
  font-size: 16px;
  width: 100%;
  border: none;
  cursor: pointer;
  border-radius: 10px;
  min-height: 40px;
  border: 1px solid ${theme.colors.blackgray};
  opacity: 0.8;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 1;
  }
`

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: absolute;
  max-height: 250px;
  min-width: 250px;
  overflow: auto;
  background-color: ${theme.colors.white};
  width: 100%;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.3);
  z-index: 1;
  border-radius: 10px;
  top: calc(100% + 10px);
  left: 0;
`

const DropdownItem = styled.a`
  color: black;
  padding: 12px 16px;
  text-decoration: none;
  display: block;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background-color: ${theme.colors.gray};
  }
`
type KindType = 'parts' | 'groups' | 'role' | 'roleSearch'
type DropdownProps = {
  currentItem: number | string
  kind: KindType
  partNo?: number
  setCurrentItem: (value: number | string) => void
}
type PartsType = { partId: number; partName: string }
type GropusType = { groupId: number; groupName: string }
type RolesType = string[]
type ItemType = { id: number | string; name: string }

const roleItems = [
  { id: 'ROLE_USER', name: 'ROLE_USER' },
  { id: 'ROLE_ADMIN', name: 'ROLE_ADMIN' },
  { id: 'ROLE_ADMIN_GROUP', name: 'ROLE_ADMIN_GROUP' },
  { id: 'ROLE_ADMIN_PART', name: 'ROLE_ADMIN_PART' },
  { id: 'ROLE_ADMIN_SUPER', name: 'ROLE_ADMIN_SUPER' },
]
// 슈퍼 관리자: ROLE_ADMIN_SUPER
// 소속 관리자: ROLE_ADMIN_PART
// 그룹 관리자: ROLE_ADMIN_GROUP
// 일반 사용자: ROLE_USER

export const Dropdown: React.FC<DropdownProps> = ({ kind, partNo, currentItem, setCurrentItem }) => {
  const [$isOpen, set$isOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    data: { data: serverData = [] } = {},
    isFetched,
    isLoading,
    isError
  } = kind === 'parts' ? useGetPartsAll({ enabled: kind === 'parts' })
    : kind === 'groups' ? useGetGroupByPart((partNo as number)?.toString(), { enabled: kind === 'groups' && partNo !== -1 })
    : kind === 'role' ? useGetRegisterRoles()
                      : useGetSearchRoles()

  const toggleDropdown = () => {
    set$isOpen(!$isOpen)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      set$isOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const items = useMemo(
    () =>
      kind === 'parts'
        ? serverData.map((item: PartsType) => ({ id: item.partId, name: item.partName }))
        : kind === 'groups'
          ? serverData.map((item: GropusType) => ({ id: item.groupId, name: item.groupName }))
          : kind === 'role'
            ? serverData.map((item: RolesType) => ({ id: item, name: item }))
            : kind === 'roleSearch'
              ? [{id:"",name:"-"},...serverData.map((item: RolesType) => ({ id: item, name: item }))]
              : [],
    [serverData]
  )

  if (isError) return <>에러</>

  const getName = (items: ItemType[], currentItem: number | string) => (items.find((item: ItemType) => item.id === currentItem) ? items.find((item: ItemType) => item.id === currentItem)?.name : '-')

  return (
    <DropdownContainer ref={dropdownRef} $isLoading={isLoading} >
      <DropdownButton onClick={toggleDropdown} type={"button"}>{isLoading ? <LoadingDots $color={theme.colors.green}/> : getName(items, currentItem)}</DropdownButton>
      <DropdownMenu $isOpen={$isOpen}>
        {items.map((item: ItemType, index: number) => (
          <DropdownItem
            key={index}
            onClick={() => {
              // item.onClick()
              setCurrentItem(item.id)
              set$isOpen(false)
            }}
          >
            {item.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  )
}

type SearchDropdownProps = {
  currentItem: string
  setCurrentItem: (value: string) => void
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({ currentItem, setCurrentItem }) => {
  const [$isOpen, set$isOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timerId = useRef<NodeJS.Timeout | null>(null);

  const {
    data: { data: serverData = [] } = {},
    isFetched,
    isLoading,
    isError
  } =  useGetSearchRoles()

  const toggleDropdown = () => {
    set$isOpen(!$isOpen)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      set$isOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const items = useMemo(
    () => [{name:"Gg", id :1},{name:"Gg", id :1},{name:"Gg", id :1},{name:"Gg", id :1},{name:"Gg", id :1},{name:"Gg", id :1},{name:"Gg", id :1},{name:"Gg", id :1}],
    [serverData]
  )

  if (isError) return <>에러</>

  const debouncedSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(timerId.current) clearTimeout(timerId.current);
    setCurrentItem(e.target.value)
    timerId.current = setTimeout(()=>{console.log(e.target.value)},500)
  }
  

  return (
    <DropdownContainer ref={dropdownRef} $isLoading={isLoading}>
      <DropdownButton onClick={toggleDropdown} type={"button"}>-</DropdownButton>
      <SearchInput onClick={toggleDropdown} value={currentItem} onChange={debouncedSearch} />
      <DropdownMenu $isOpen={$isOpen}>
        {items.map((item: ItemType, index: number) => (
          <DropdownItem
            key={index}
            onClick={() => {
              // item.onClick()
              setCurrentItem(item.name)
              set$isOpen(false)
            }}
          >
            {item.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  )
}

const SearchInput = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid ${theme.colors.blackgray};
  font-size: 14px;
`

type DropdownProps_History = {
  currentItem: number | string
  kind?: KindType
  groupId: number
  setCurrentItem: (value: number | string) => void
}


export const Dropdown_Device: React.FC<DropdownProps_History> = ({ kind, groupId, currentItem, setCurrentItem }) => {
  const [$isOpen, set$isOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    data:serverData= [],
    isFetched,
    isLoading,
    isError
  } = useGetGroupDeviceList(groupId)

  const toggleDropdown = () => {
    set$isOpen(!$isOpen)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      set$isOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])



  if (isError) return <>에러</>

  const getName = (items: any[], currentItem: number | string) => (items.find((item: any) => item.deviceId === currentItem) ? items.find((item: any) => item.deviceId === currentItem)?.driveNumber : '-')

  return (
    <DropdownContainer ref={dropdownRef} $isLoading={isLoading} >
      <DropdownButton onClick={toggleDropdown} type={"button"}>{isLoading ? <LoadingDots $color={theme.colors.green}/> : getName(serverData, currentItem)}</DropdownButton>
      <DropdownMenu $isOpen={$isOpen}>
        {serverData.map((item: any, index: number) => (
          <DropdownItem
            key={index}
            onClick={() => {
              // item.onClick()
              setCurrentItem(item.deviceId)
              set$isOpen(false)
            }}
          >
            {item.driveNumber}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  )
}